<template>
  <div class="unsubscribe-page">
    <section class="unsub-header">
      <div class="logo-wrap">
        <img
          src="@/assets/images/_Rx-Pros-Logo-01.png"
          srcset="@/assets/images/_Rx-Pros-Logo-01-p-500.png 500w, @/assets/images/_Rx-Pros-Logo-01-p-800.png 800w, @/assets/images/_Rx-Pros-Logo-01-p-1080.png 1080w, @/assets/images/_Rx-Pros-Logo-01.png 2083w"
          sizes="(max-width: 200px) 100vw, 200px"
          alt="RxPros"
          class="logo-img"
        />
      </div>
    </section>

    <section class="unsub-body">
      <div class="unsub-card">
        <!-- Initial state: confirm unsubscribe -->
        <template v-if="state === 'confirm'">
          <h1 class="unsub-title">Unsubscribe from emails</h1>
          <p class="unsub-text">
            Click the button below to confirm and we'll stop sending you marketing emails from RxPros.
          </p>
          <p class="unsub-note">
            You'll still receive important emails about your account, orders, and treatment.
          </p>
          <button
            type="button"
            class="unsub-btn primary"
            :disabled="submitting"
            @click="confirmUnsubscribe"
          >
            <span v-if="!submitting">Unsubscribe</span>
            <span v-else>Unsubscribing...</span>
          </button>
        </template>

        <!-- Success state: unsubscribed -->
        <template v-else-if="state === 'success'">
          <div class="unsub-check">
            <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <h1 class="unsub-title">You've been unsubscribed</h1>
          <p class="unsub-text">
            We're sorry to see you go. You'll no longer receive marketing emails from RxPros.
          </p>
          <p class="unsub-note">
            You'll still get important account, order, and treatment emails.
          </p>

          <div class="action-stack">
            <a href="/" class="unsub-btn primary">Return to RxPros</a>
            <router-link to="/login" class="unsub-btn-text">
              Log in to manage your account
            </router-link>
          </div>

          <p class="unsub-note resub-note">
            Changed your mind?
            <button
              type="button"
              class="resub-link"
              :disabled="submitting"
              @click="resubscribe"
            >
              {{ submitting ? 'Resubscribing...' : 'Resubscribe' }}
            </button>
          </p>
        </template>

        <!-- Resubscribed state -->
        <template v-else-if="state === 'resubscribed'">
          <div class="unsub-check">
            <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <h1 class="unsub-title">You're resubscribed</h1>
          <p class="unsub-text">
            Welcome back! You'll continue to receive emails from RxPros.
          </p>

          <div class="action-stack">
            <a href="/" class="unsub-btn primary">Return to RxPros</a>
            <router-link to="/login" class="unsub-btn-text">
              Log in to manage your account
            </router-link>
          </div>
        </template>

        <!-- Error state -->
        <template v-else-if="state === 'error'">
          <div class="unsub-error-icon">
            <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <h1 class="unsub-title">{{ errorTitle }}</h1>
          <p class="unsub-text">{{ errorMessage }}</p>
          <button type="button" class="unsub-btn primary" @click="retry">
            Try again
          </button>
        </template>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios';

// Fresh axios instance with no interceptors, so a 401 from the backend
// does not trigger the global redirect to /login. This page is public.
const publicAxios = axios.create();

export default {
  name: 'UnsubscribePage',
  data() {
    return {
      baseUrl: process.env.VUE_APP_API_ENDPOINT,
      state: 'confirm',
      submitting: false,
      errorTitle: '',
      errorMessage: '',
    };
  },
  computed: {
    userId() {
      return this.$route.params.userId;
    },
  },
  methods: {
    async confirmUnsubscribe() {
      if (!this.userId) {
        this.showError(
          'This link looks broken',
          "We couldn't find the right info in your unsubscribe link. Please contact our team and we'll take care of it for you."
        );
        return;
      }
      await this.updateEmailPreference(true, 'success');
    },
    async resubscribe() {
      if (!this.userId) {
        this.showError(
          'This link looks broken',
          "We couldn't find the right info in your unsubscribe link. Please contact our team and we'll take care of it for you."
        );
        return;
      }
      await this.updateEmailPreference(false, 'resubscribed');
    },
    async updateEmailPreference(optOut, successState) {
      this.submitting = true;
      try {
        // Fully anonymous: never attach the logged-in user's token.
        // The userId in the body is the only source of truth, so a logged-in
        // user clicking an unsubscribe link for someone else's account works correctly.
        const response = await publicAxios.post(
          `${this.baseUrl}/api/unsubscribe-email`,
          {
            userId: this.userId,
            opt_out_email: optOut,
          }
        );

        if (response.data && response.data.success) {
          this.state = successState;
        } else {
          this.showError(
            "We couldn't save your changes",
            "Something didn't work on our end. Please wait a moment and tap Try again. If you keep seeing this, please contact our team for help."
          );
        }
      } catch (err) {
        this.showError(
          "We couldn't save your changes",
          "Something didn't work on our end. Please wait a moment and tap Try again. If you keep seeing this, please contact our team for help."
        );
      } finally {
        this.submitting = false;
      }
    },
    showError(title, message) {
      this.errorTitle = title;
      this.errorMessage = message;
      this.state = 'error';
    },
    retry() {
      this.errorTitle = '';
      this.errorMessage = '';
      this.state = 'confirm';
    },
  },
};
</script>

<style scoped>
.unsubscribe-page {
  min-height: 100vh;
  background-color: var(--f9-grey, #f9f9f9);
  display: flex;
  flex-direction: column;
  font-family: var(--roboto, Roboto, sans-serif);
}

.unsub-header {
  padding: 32px 24px 16px;
  display: flex;
  justify-content: center;
}

.logo-wrap {
  width: 100%;
  max-width: 200px;
}

.logo-img {
  width: 100%;
  height: auto;
  display: block;
}

.unsub-body {
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 20px 80px;
}

.unsub-card {
  width: 100%;
  max-width: 520px;
  background-color: var(--white, #ffffff);
  border: 1px solid var(--df-grey, #dfdfdf);
  border-radius: 16px;
  padding: 40px 32px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(36, 36, 36, 0.04);
}

.unsub-title {
  font-family: var(--roboto, Roboto, sans-serif);
  font-size: 26px;
  font-weight: 600;
  color: var(--primary-black, #242424);
  margin: 0 0 16px;
  line-height: 1.25;
}

.unsub-text {
  font-size: 16px;
  color: var(--primary-black, #242424);
  line-height: 1.5;
  margin: 0 0 12px;
}

.unsub-note {
  font-size: 14px;
  color: var(--666-grey, #666);
  line-height: 1.5;
  margin: 0 0 24px;
}

.resub-note {
  margin: 24px 0 0;
}

.unsub-btn {
  display: inline-block;
  width: 100%;
  max-width: 320px;
  padding: 14px 24px;
  font-family: var(--roboto, Roboto, sans-serif);
  font-size: 16px;
  font-weight: 600;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  transition: background-color 0.15s ease;
  margin-top: 8px;
  text-decoration: none;
  text-align: center;
  box-sizing: border-box;
}

.unsub-btn.primary {
  background-color: var(--darkbluetext, #30989d);
  color: var(--white, #ffffff);
}

.unsub-btn.primary:hover:not(:disabled) {
  background-color: var(--dark-bluegreen, #4e7882);
}

.unsub-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.unsub-btn-text {
  display: inline-block;
  background: none;
  border: none;
  padding: 4px 8px;
  font-family: var(--roboto, Roboto, sans-serif);
  font-size: 15px;
  font-weight: 500;
  color: var(--darkbluetext, #30989d);
  text-decoration: underline;
  cursor: pointer;
}

.unsub-btn-text:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-stack {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.unsub-check {
  color: var(--logo-green, #42cad1);
  margin: 0 auto 16px;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: rgba(66, 202, 209, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
}

.unsub-error-icon {
  color: #d9534f;
  margin: 0 auto 16px;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: rgba(217, 83, 79, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.resub-link {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: var(--darkbluetext, #30989d);
  text-decoration: underline;
  cursor: pointer;
}

.resub-link:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 480px) {
  .unsub-card {
    padding: 32px 20px;
    border-radius: 12px;
  }

  .unsub-title {
    font-size: 22px;
  }

  .unsub-text {
    font-size: 15px;
  }
}
</style>
